import { Paper, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import { PageUid } from "../../../context/CustomisationContext";
import { usePortal } from "../../../context/PortalsContext";
import { getAllPagesForPortal } from "../../../services/api/apiClient";
import { Page } from "../../../services/api/types/portalConfiguration/portal";
import { gray } from "../../../theme/themePrimitives";
import { CustomisationTabSection } from "./components/CustomisationTabSection/CustomisationTabSection";
import { useToast } from "../../../context/ToastContext";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { extractErrorMessage } from "../../../utils/errorUtils";

interface PortalDesignProps {}

const PAGE_TABS: Record<PageUid, readonly string[]> = {
  GPC: [
    "Logo",
    "Background",
    "Text colors",
    "Navbar",
    "Buttons",
    "Assignment status colors",
  ],
  HOME: [
    "Background",
    "Text colors",
    "Buttons",
    "Assignment status colors",
    "Cards",
  ],
  LOGIN: ["Logo", "Background", "Text colors", "Buttons", "Input fields"],
  CERTIFICATES: ["Logo", "Background", "Text colors", "Buttons"],
  PROFILE: ["Background", "Text colors", "Buttons"],
};

const PAGE_ORDER: PageUid[] = [
  "GPC",
  "LOGIN",
  "HOME",
  "CERTIFICATES",
  "PROFILE",
];

export const PortalDesign: React.FC<PortalDesignProps> = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const { showToast } = useToast();
  const { portals } = usePortal();

  const currentPage = pages[activeTab];
  const currentTabs = currentPage
    ? PAGE_TABS[currentPage.portalPageUid as PageUid]
    : undefined;

  useEffect(() => {
    const fetchPages = async () => {
      if (!portals.selectedPortal) return;

      try {
        const response = await getAllPagesForPortal(
          portals.selectedPortal.portalUid,
        );

        const sortedPages = response
          .filter((p: Page) => p.isVisible)
          .sort(
            (a: Page, b: Page) =>
              PAGE_ORDER.indexOf(a.portalPageUid as PageUid) -
              PAGE_ORDER.indexOf(b.portalPageUid as PageUid),
          );

        setPages(sortedPages);
      } catch (error: any) {
        showToast(extractErrorMessage(error, "Failed to load pages"), "error");
      }
    };

    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.selectedPortal]);

  usePageMetadata({
    title: "Portal design",
    breadcrumbs: [
      {
        label: "Portal configuration",
        path: `/portal-configuration`,
      },
      {
        label: "Portal design",
      },
    ],
  });

  return (
    <Paper sx={{ pt: 1 }}>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        textColor="primary"
        indicatorColor="primary"
        sx={{
          minHeight: "unset",
          borderBottom: `1px solid ${gray[200]}`,
          fontWeight: "600",
        }}
      >
        {pages.map((page) => (
          <Tab key={page.portalPageUid} label={page.name} />
        ))}
      </Tabs>
      {currentPage && currentTabs && currentTabs.length > 0 && (
        <CustomisationTabSection
          tabSection={currentPage.portalPageUid as PageUid}
          tabs={currentTabs}
        />
      )}
    </Paper>
  );
};
